import React from "react";
import { AUTH } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();
  const { search } = useLocation();
  const denied = new URLSearchParams(search).get("denied");
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const handleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid={AUTH.loginPage} className="min-h-screen flex items-center justify-center p-6">
      <div className="grid lg:grid-cols-5 gap-0 w-full max-w-6xl surface-card rounded-md overflow-hidden enter-up">
        {/* Left: Brand */}
        <div className="lg:col-span-3 p-10 lg:p-14 border-b lg:border-b-0 lg:border-r border-zinc-800 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20 pointer-events-none"
               style={{ background: "radial-gradient(ellipse at top left, rgba(249,115,22,0.4), transparent 50%)" }} />
          <div className="relative">
            <div className="flex items-center gap-3 mb-12">
              <div className="h-9 w-9 rounded-sm bg-brand flex items-center justify-center font-heading text-black font-black text-lg">3</div>
              <div>
                <div className="label-tag">360 Digital Marketing</div>
                <div className="font-heading text-lg font-bold tracking-tight">SignFlow360™</div>
              </div>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight">
              Quote, mockup,<br/>
              <span className="text-brand">close deals</span>.
            </h1>
            <p className="mt-6 text-zinc-400 max-w-md text-base leading-relaxed">
              Internal channel-letter estimator and storefront mockup tool. Built for the field — measure, price, and send a customer-ready proposal in minutes.
            </p>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {[
                { k: "$18", v: "PER INCH" },
                { k: "<3 min", v: "PER QUOTE" },
                { k: "PDF", v: "PROPOSAL" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-mono text-2xl text-white">{s.k}</div>
                  <div className="label-tag mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Sign in */}
        <div className="lg:col-span-2 p-10 lg:p-12 flex flex-col justify-center bg-[#0c0c0e]">
          <div className="label-tag mb-3">Internal Access</div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Sign in to continue</h2>
          <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
            Use your Google account to access projects, mockups and proposals.
          </p>

          {denied && (
            <div data-testid="auth-denied-banner" className="mt-6 flex items-start gap-3 p-3 border border-amber-700/60 bg-amber-950/30 rounded-md text-amber-200 text-xs leading-relaxed">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{denied}</span>
            </div>
          )}

          <button
            data-testid={AUTH.signInBtn}
            onClick={handleSignIn}
            className="group mt-8 w-full inline-flex items-center justify-center gap-3 h-12 px-5 bg-white text-zinc-900 font-medium rounded-md hover:bg-zinc-100 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#EA4335" d="M12 10.2v3.8h5.3c-.2 1.3-1.6 3.9-5.3 3.9-3.2 0-5.8-2.7-5.8-5.9s2.6-5.9 5.8-5.9c1.8 0 3 .8 3.7 1.4l2.5-2.4C16.6 3.6 14.5 2.6 12 2.6 6.8 2.6 2.6 6.8 2.6 12s4.2 9.4 9.4 9.4c5.4 0 9-3.8 9-9.2 0-.6-.1-1.1-.2-1.6H12z"/>
            </svg>
            <span>Continue with Google</span>
          </button>

          <div className="mt-10 text-[11px] text-zinc-600 leading-relaxed">
            Restricted to <span className="text-zinc-400 font-mono">@360dma.com</span> Google accounts. SignFlow360™ is a tool of 360 Digital Marketing for staff use only.
          </div>
        </div>
      </div>
    </div>
  );
}
